import { chen, estimateEquity } from "./equity";
import { proPlusDecide } from "./aiProPlus";
import { proPlusPlusDecide } from "./aiProPlusPlus";

// AI receives a "view" built by engine.viewFor(): its own hole cards + public info only.
const SKILL = {
  novice: { noise: 3, posW: 0.3, openAdj: -2.5, limp: 0.55, aggr: 0.35, bluff: 0.04, margin: -0.07, respect: 0, push: false, iters: 150 },
  regular: { noise: 1.2, posW: 0.8, openAdj: 0, limp: 0.05, aggr: 0.65, bluff: 0.09, margin: 0.02, respect: 0.07, push: true, iters: 300 },
  pro: { noise: 0.6, posW: 1.2, openAdj: -0.5, limp: 0, aggr: 0.8, bluff: 0.14, margin: 0.0, respect: 0.1, push: true, iters: 600 },
};
const POS_BONUS = { EP: -1, MP: 0, CO: 1, BTN: 2, SB: 0.5, BB: 0 };
const OPEN = { EP: 9, MP: 8, CO: 7, BTN: 6, SB: 7, BB: 8 };
const LATE = ["BTN", "CO", "SB"];
const rnd = Math.random;

const check = { type: "check" };
const passive = (v) => (v.toCall === 0 ? check : { type: "call" });
const fold = (v) => (v.toCall === 0 ? check : { type: "fold" });
const allIn = (v) => (v.canRaise ? { type: "raise", amount: v.maxTo } : passive(v));

function raiseTo(v, amt) {
  if (!v.canRaise) return passive(v);
  let to = Math.max(v.minRaiseTo, Math.round(amt / v.sbUnit) * v.sbUnit);
  if (to >= v.maxTo * 0.8) to = v.maxTo;
  return { type: "raise", amount: Math.min(to, v.maxTo) };
}

function preflop(v, k, novice) {
  const score = chen(v.hole) + k.posW * POS_BONUS[v.pos] + (rnd() * 2 - 1) * k.noise;
  const eff = v.effBB;
  const pressure = v.toCall / (v.stack + v.bet);

  if (k.push && eff <= 12) {
    const thr = 6.5 + eff * 0.35;
    if (v.raiseCount === 0) {
      if (score >= thr) return allIn(v);
      if (LATE.includes(v.pos) && rnd() < k.bluff) return allIn(v);
      return fold(v);
    }
    const odds = v.toCall / (v.pot + v.toCall);
    return score >= thr + 2 - (odds < 0.34 ? 1.5 : 0) ? allIn(v) : fold(v);
  }

  if (v.raiseCount === 0) {
    const thr = OPEN[v.pos] + k.openAdj + v.limpers * 0.5;
    if (score >= thr) {
      if (rnd() < k.limp) return passive(v);
      return raiseTo(v, v.bb * (2.5 + v.limpers) + (v.pos === "SB" ? v.bb * 0.5 : 0));
    }
    if (v.toCall === 0) return check;
    if (score >= thr - 2.5 && (rnd() < k.limp || (v.pos === "SB" && rnd() < 0.35))) return passive(v);
    if (LATE.includes(v.pos) && v.limpers === 0 && rnd() < k.bluff * 2.5) return raiseTo(v, v.bb * 2.5);
    return fold(v);
  }

  const sizeBB = v.currentBet / v.bb;
  if (v.raiseCount === 1) {
    const threeBet = 12;
    const callT = 9 + (sizeBB > 4 ? 1 : 0) + pressure * 6 - (v.pos === "BB" ? 1.5 : 0) - (novice ? 1.5 : 0);
    if (score >= threeBet && rnd() < k.aggr + 0.2) return raiseTo(v, v.currentBet * (LATE.includes(v.pos) ? 3 : 3.5));
    if (score >= callT) return passive(v);
    if (LATE.includes(v.pos) && rnd() < k.bluff * 0.6) return raiseTo(v, v.currentBet * 3);
    return fold(v);
  }
  if (score >= 14) return rnd() < 0.6 ? allIn(v) : raiseTo(v, v.currentBet * 2.3);
  if (score >= 11.5 + pressure * 3 - (novice ? 2 : 0)) return passive(v);
  return fold(v);
}

function postflop(v, k) {
  const nOpp = Math.max(1, v.nOpp);
  const eq = estimateEquity(v.hole, v.board, nOpp, k.iters) + (rnd() * 2 - 1) * k.noise * 0.03;
  const draw = v.street !== "river";
  const bet = (frac) => raiseTo(v, v.pot * frac);

  if (v.toCall === 0) {
    if (eq > 0.68) return rnd() < 0.88 ? bet(0.55 + rnd() * 0.25) : check;
    if (eq > 0.5 && rnd() < k.aggr * 0.7) return bet(0.5);
    let bf = k.bluff * (v.inPosition ? 1.6 : 1) * (nOpp === 1 ? 1.5 : 0.5);
    if (draw && eq > 0.3) bf *= 2;
    if (rnd() < bf) return bet(0.5 + rnd() * 0.2);
    return check;
  }
  const potBefore = Math.max(1, v.pot - v.toCall);
  const betRatio = Math.min(1.5, v.toCall / potBefore);
  const adj = eq - k.respect * betRatio * (v.raiseCount > 1 ? 1.5 : 1);
  const odds = v.toCall / (v.pot + v.toCall);
  if (adj > 0.78 && rnd() < k.aggr) return raiseTo(v, v.currentBet + (v.pot + v.toCall) * 0.8);
  if (adj > odds + k.margin) return passive(v);
  if (nOpp === 1 && draw && eq > 0.25 && rnd() < k.bluff * 0.35) return raiseTo(v, v.currentBet * 2.5);
  return fold(v);
}

export function aiDecide(view, skill) {
  if (skill === "proplus") return proPlusDecide(view);
  if (skill === "proplusplus") return proPlusPlusDecide(view);
  const k = SKILL[skill] || SKILL.regular;
  return view.street === "preflop" ? preflop(view, k, skill === "novice") : postflop(view, k);
}
